import type {
	CopilotMessage,
	CopilotSession,
	CopilotArtifact,
	CopilotErrorState
} from "@/Contracts/Copilot";

export type CopilotActivityKind = "thinking" | "tool" | "drafting" | "writing";

export interface CopilotActivity {
	label: string;
	kind: CopilotActivityKind;
}

export interface PendingAssistantTurn {
	id: string;
	text: string;
	artifacts: CopilotArtifact[];
	toolCalls: Array<{
		tool: string;
		startedAt: number;
		finishedAt?: number;
		ok?: boolean;
	}>;
	activity: CopilotActivity;
}

export interface PendingEmailDraft {
	subject: string;
	body: string;
	toContactId: string | null;
	citeOpportunityId: string | null;
}

export interface AppliedArtifactSignal {
	entityType: "opportunity" | "client" | "contact";
	entityId: string;
	at: number;
}

export type ArtifactFailureReason =
	| "editor_closed"
	| "document_mismatch"
	| "stale_selection"
	| "apply_failed";

export type EditorNoticeReason = "editor_closed" | "document_mismatch";

export interface CopilotState {
	isOpen: boolean;
	session: CopilotSession | null;
	messages: CopilotMessage[];
	pending: PendingAssistantTurn | null;
	appliedArtifacts: Record<string, "applied" | "failed" | "discarded">;
	artifactFailureReasons: Record<string, ArtifactFailureReason>;
	error: CopilotErrorState | null;
	editorNotice: EditorNoticeReason | null;
	pendingEmailDraft: PendingEmailDraft | null;
	pendingEmailDraftToken: number;
	lastAppliedArtifact: AppliedArtifactSignal | null;
	pricingScopeBlockId: string | null;
}

export const CopilotActionTypes = {
	OPEN: "COPILOT/OPEN",
	CLOSE: "COPILOT/CLOSE",
	SET_SESSION: "COPILOT/SET_SESSION",
	CLEAR_SESSION: "COPILOT/CLEAR_SESSION",
	START_USER_TURN: "COPILOT/START_USER_TURN",
	START_PENDING_TURN: "COPILOT/START_PENDING_TURN",
	APPEND_TOKEN: "COPILOT/APPEND_TOKEN",
	APPEND_TOOL_START: "COPILOT/APPEND_TOOL_START",
	APPEND_TOOL_END: "COPILOT/APPEND_TOOL_END",
	APPEND_ARTIFACT: "COPILOT/APPEND_ARTIFACT",
	COMMIT_PENDING_TURN: "COPILOT/COMMIT_PENDING_TURN",
	SET_ERROR: "COPILOT/SET_ERROR",
	MARK_ARTIFACT_APPLIED: "COPILOT/MARK_ARTIFACT_APPLIED",
	MARK_ARTIFACT_FAILED: "COPILOT/MARK_ARTIFACT_FAILED",
	MARK_ARTIFACT_DISCARDED: "COPILOT/MARK_ARTIFACT_DISCARDED",
	RESET_CONVERSATION: "COPILOT/RESET_CONVERSATION",
	SET_PENDING_EMAIL_DRAFT: "COPILOT/SET_PENDING_EMAIL_DRAFT",
	CLEAR_PENDING_EMAIL_DRAFT: "COPILOT/CLEAR_PENDING_EMAIL_DRAFT",
	SIGNAL_ARTIFACT_APPLIED: "COPILOT/SIGNAL_ARTIFACT_APPLIED",
	APPEND_STATUS: "COPILOT/APPEND_STATUS",
	SET_PRICING_SCOPE: "COPILOT/SET_PRICING_SCOPE",
	CLEAR_PRICING_SCOPE: "COPILOT/CLEAR_PRICING_SCOPE",
	SET_EDITOR_NOTICE: "COPILOT/SET_EDITOR_NOTICE",
	CLEAR_EDITOR_NOTICE: "COPILOT/CLEAR_EDITOR_NOTICE"
} as const;
